'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertStatusIcon,
  CheckStatusIcon,
  CloseIcon,
  InfoStatusIcon,
} from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';
import { useEntranceMotion } from '../../utils/useEntranceMotion';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  readonly tone?: ToastTone;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly durationMs?: number;
  readonly action?: { readonly label: string; readonly onPress: () => void };
}

interface Toast extends ToastInput {
  readonly id: string;
}

interface ToastContextValue {
  readonly push: (toast: ToastInput) => string;
  readonly dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const TONE: Record<ToastTone, { iconClass: string; Icon: typeof InfoStatusIcon }> = {
  info: { iconClass: 'text-info bg-info/15', Icon: InfoStatusIcon },
  success: { iconClass: 'text-success bg-success/15', Icon: CheckStatusIcon },
  warning: { iconClass: 'text-warning bg-warning/15', Icon: AlertStatusIcon },
  danger: { iconClass: 'text-danger bg-danger/15', Icon: AlertStatusIcon },
};

interface ToastViewProps {
  readonly toast: Toast;
  readonly onDismiss: (id: string) => void;
}

function ToastView({ toast, onDismiss }: ToastViewProps) {
  const { t } = useI18n();
  const { Icon, iconClass } = TONE[toast.tone ?? 'info'];
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  useEntranceMotion(ref);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const d = toast.durationMs ?? 5000;
    if (d <= 0 || paused) return;
    timer.current = setTimeout(() => onDismiss(toast.id), d);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [toast.id, toast.durationMs, onDismiss, paused]);

  return (
    <div
      ref={ref}
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      aria-atomic="true"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      className={cn(
        'pointer-events-auto flex items-start gap-3 p-3 pe-2 rounded-xl bg-surface text-ink',
        'border border-hairline-2 shadow-[0_16px_40px_-20px_rgba(0,0,0,0.20)] min-w-0 w-full max-w-sm',
      )}
    >
      <span
        className={cn(
          'shrink-0 size-7 rounded-full inline-flex items-center justify-center',
          iconClass,
        )}
      >
        <Icon size={14} color="currentColor" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-ink leading-snug">{toast.title}</div>
        {toast.description ? (
          <div className="text-[12px] text-ink-2 leading-snug mt-0.5">{toast.description}</div>
        ) : null}
      </div>
      {toast.action ? (
        <button
          type="button"
          onClick={() => {
            toast.action!.onPress();
            onDismiss(toast.id);
          }}
          className="shrink-0 px-2 min-h-9 text-caption font-bold uppercase tracking-wider text-accent-text rounded-md hover:bg-accent-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        aria-label={t('common:feedback.dismiss', { defaultValue: 'Dismiss' })}
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 size-9 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
      >
        <CloseIcon size={14} color="currentColor" />
      </button>
    </div>
  );
}

export interface ToastProviderProps {
  readonly children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((toast: ToastInput) => {
    counter.current += 1;
    const id = `t-${Date.now()}-${counter.current}`;
    setToasts((cur) => [...cur, { ...toast, id }]);
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              role="region"
              aria-label={t('common:feedback.notifications', { defaultValue: 'Notifications' })}
              className="pointer-events-none fixed top-3 inset-x-3 sm:inset-x-auto sm:top-4 sm:end-4 sm:w-96 max-w-[calc(100vw-1.5rem)] z-[100] flex flex-col gap-2"
            >
              {toasts.map((t) => (
                <ToastView key={t.id} toast={t} onDismiss={dismiss} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}
